import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  useToast,
} from '@chakra-ui/react';
import { showToast, Text } from '@diamond/shared/ui';
import { useApplyVoucher } from '@diamond/sol-buyer-context';
import { ArrowBackOutlined } from '@mui/icons-material';
import { type NextRouter } from 'next/router';
import { FC, useState } from 'react';

interface NavHeaderVoucherProps {
  router: NextRouter;
}

export const NavHeaderVoucher: FC<NavHeaderVoucherProps> = ({ router }) => {
  const [voucherCode, setVoucherCode] = useState('');
  const toast = useToast();
  const { mutate: applyVoucher, isPending } = useApplyVoucher();

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setVoucherCode(text.trim());
    } catch (error) {
      showToast(
        toast,
        'error',
        'Gagal menempel kode - Tidak dapat mengakses clipboard',
        undefined,
        'bottom',
        3000
      );
    }
  };

  const handleApply = () => {
    if (!voucherCode.trim()) {
      showToast(
        toast,
        'warning',
        'Kode voucher kosong - Silakan masukkan kode voucher',
        undefined,
        'bottom',
        3000
      );
      return;
    }

    applyVoucher(voucherCode.trim(), {
      onSuccess: (data: unknown) => {
        const message =
          data && typeof data === 'object' && 'message' in data
            ? (data as { message: string }).message
            : 'Voucher berhasil diterapkan';

        showToast(
          toast,
          'success',
          `Berhasil! ${message}`,
          undefined,
          'bottom',
          3000
        );
        setVoucherCode('');
      },
      onError: (error) => {
        showToast(
          toast,
          'error',
          `Gagal menerapkan voucher - ${
            error instanceof Error ? error.message : 'Terjadi kesalahan'
          }`,
          undefined,
          'bottom',
          3000
        );
      },
    });
  };

  return (
    <Flex w="full" flexDirection="column" py="4" gap="2" alignItems="start">
      <HStack spacing="2" flex="1">
        <IconButton
          onClick={() => router.back()}
          colorScheme="white"
          aria-label="Kembali"
          variant="solid"
          bg="transparent"
          _hover={{ bgColor: 'azure' }}
          icon={<ArrowBackOutlined fontSize="medium" />}
          minW="auto"
          h="auto"
          p="0"
        />
        <Text color="white" mx="1" fontSize="20px" fontWeight="600">
          |
        </Text>
        <Text color="white" fontSize="20px" fontWeight="600">
          Voucher
        </Text>
      </HStack>
      {router.pathname === '/voucher' && (
        <Flex justifyContent="space-between" flex="1" w="full">
          <Box flex="2" position="relative" mr="8px">
            <Input
              placeholder="Masukkan kode voucher"
              value={voucherCode}
              onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
              bg="white"
              border="none"
              borderRadius="8px"
              fontSize="14px"
              h="40px"
              pr="70px"
              _placeholder={{ color: 'gray.400' }}
            />
            <Button
              zIndex="5"
              position="absolute"
              right="8px"
              top="50%"
              transform="translateY(-50%)"
              variant="link"
              color="#007AFF"
              fontSize="14px"
              fontWeight="600"
              onClick={handlePaste}
              h="auto"
              minW="auto"
              p="0"
            >
              Tempel
            </Button>
          </Box>

          <Button
            colorScheme="gray"
            variant="solid"
            bg={voucherCode.trim() ? 'white' : 'gray.400'}
            color={voucherCode.trim() ? '#007AFF' : 'gray.600'}
            fontSize="14px"
            fontWeight="600"
            h="40px"
            px="6"
            borderRadius="8px"
            onClick={handleApply}
            isDisabled={!voucherCode.trim() || isPending}
            isLoading={isPending}
            _hover={{
              bg: voucherCode.trim() ? 'gray.100' : 'gray.400',
            }}
          >
            Gunakan
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default NavHeaderVoucher;
